/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AssignmentConfirmationRequiredResponse } from '../models/AssignmentConfirmationRequiredResponse';
import type { AssignmentPartialSuccessResponse } from '../models/AssignmentPartialSuccessResponse';
import type { BenefitActivationListRequest } from '../models/BenefitActivationListRequest';
import type { BenefitBudget } from '../models/BenefitBudget';
import type { BenefitBudgetOverview } from '../models/BenefitBudgetOverview';
import type { BenefitCalculatorRequest } from '../models/BenefitCalculatorRequest';
import type { BenefitCalculatorResponse } from '../models/BenefitCalculatorResponse';
import type { BenefitConfig } from '../models/BenefitConfig';
import type { BenefitEnum } from '../models/BenefitEnum';
import type { BenefitSet } from '../models/BenefitSet';
import type { BenefitSetEmployees } from '../models/BenefitSetEmployees';
import type { BenefitSetEmployeesRequest } from '../models/BenefitSetEmployeesRequest';
import type { BenefitSetRequest } from '../models/BenefitSetRequest';
import type { BookedBenefitStats } from '../models/BookedBenefitStats';
import type { CompanyBenefitStats } from '../models/CompanyBenefitStats';
import type { CompanyCustomBenefit } from '../models/CompanyCustomBenefit';
import type { CompanyCustomBenefitAssignmentEmployee } from '../models/CompanyCustomBenefitAssignmentEmployee';
import type { CompanyCustomBenefitAssignmentEmployeeRequest } from '../models/CompanyCustomBenefitAssignmentEmployeeRequest';
import type { CompanyCustomBenefitAssignmentEmployeeUpdate } from '../models/CompanyCustomBenefitAssignmentEmployeeUpdate';
import type { CompanyCustomBenefitDetail } from '../models/CompanyCustomBenefitDetail';
import type { CompanyCustomBenefitPatch } from '../models/CompanyCustomBenefitPatch';
import type { CompanyCustomBenefitPatchRequest } from '../models/CompanyCustomBenefitPatchRequest';
import type { CompanyCustomBenefitRequest } from '../models/CompanyCustomBenefitRequest';
import type { CustomBenefitPendingEmployeeCreateRequest } from '../models/CustomBenefitPendingEmployeeCreateRequest';
import type { CustomBenefitPendingEmployeeDestroyRequest } from '../models/CustomBenefitPendingEmployeeDestroyRequest';
import type { NextMonthBenefitSet } from '../models/NextMonthBenefitSet';
import type { PaginatedBenefitSetEmployeesList } from '../models/PaginatedBenefitSetEmployeesList';
import type { PaginatedBenefitSetList } from '../models/PaginatedBenefitSetList';
import type { PaginatedCompanyCustomBenefitAssignmentEmployeeList } from '../models/PaginatedCompanyCustomBenefitAssignmentEmployeeList';
import type { PaginatedCompanyCustomBenefitList } from '../models/PaginatedCompanyCustomBenefitList';
import type { PatchedBenefitSetEmployeesRequest } from '../models/PatchedBenefitSetEmployeesRequest';
import type { PatchedBenefitSetRequest } from '../models/PatchedBenefitSetRequest';
import type { PatchedCompanyCustomBenefitAssignmentEmployeeRequest } from '../models/PatchedCompanyCustomBenefitAssignmentEmployeeRequest';
import type { PatchedCompanyCustomBenefitPatchRequest } from '../models/PatchedCompanyCustomBenefitPatchRequest';
import type { UpcomingBenefitSetsResponse } from '../models/UpcomingBenefitSetsResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class BenefitsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Benefit activation
     * Activates the given benefits for the given employee. If one benefit cannot be activated, none will be activated
     * @returns BenefitBudget
     * @throws ApiError
     */
    public benefitsActivateCreate({
        employee,
        requestBody,
    }: {
        /**
         * The employee ID for whom the benefits should be activated. 'self' will do the activation for the current user, if the user has an associated employee
         */
        employee: string,
        requestBody: BenefitActivationListRequest,
    }): CancelablePromise<Array<BenefitBudget>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/benefits/activate/',
            query: {
                'employee': employee,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * List benefit sets
     * Returns a list of all benefit sets for the specified company
     * @returns PaginatedBenefitSetList
     * @throws ApiError
     */
    public benefitsBenefitSetsList({
        company,
        limit,
        offset,
    }: {
        /**
         * The ID of the company, or 'own' to select the user's own company, if the user has an associated employee
         */
        company: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedBenefitSetList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/benefit-sets/',
            query: {
                'company': company,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Create benefit set
     * Creates a new benefit set for the specified company
     * @returns BenefitSet
     * @throws ApiError
     */
    public benefitsBenefitSetsCreate({
        company,
        requestBody,
    }: {
        /**
         * The ID of the company, or 'own' to select the user's own company, if the user has an associated employee
         */
        company: string,
        requestBody?: BenefitSetRequest,
    }): CancelablePromise<BenefitSet> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/benefits/benefit-sets/',
            query: {
                'company': company,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Retrieve benefit set
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BenefitSet
     * @throws ApiError
     */
    public benefitsBenefitSetsRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this company benefit set.
         */
        id: string,
    }): CancelablePromise<BenefitSet> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/benefit-sets/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BenefitSet
     * @throws ApiError
     */
    public benefitsBenefitSetsUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this company benefit set.
         */
        id: string,
        requestBody?: BenefitSetRequest,
    }): CancelablePromise<BenefitSet> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/benefits/benefit-sets/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BenefitSet
     * @throws ApiError
     */
    public benefitsBenefitSetsPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this company benefit set.
         */
        id: string,
        requestBody?: PatchedBenefitSetRequest,
    }): CancelablePromise<BenefitSet> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/benefits/benefit-sets/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns void
     * @throws ApiError
     */
    public benefitsBenefitSetsDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this company benefit set.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/benefits/benefit-sets/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * List benefit set's assigned employees
     * Returns a list of all assigned employees in the specified or current month for the specified benefit set
     * @returns PaginatedBenefitSetEmployeesList
     * @throws ApiError
     */
    public benefitsBenefitSetsEmployeesList({
        setId,
        accountingPeriodMonth,
        accountingPeriodYear,
        limit,
        offset,
    }: {
        setId: string,
        accountingPeriodMonth?: number,
        accountingPeriodYear?: number,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedBenefitSetEmployeesList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/benefit-sets/{set_id}/employees/',
            path: {
                'set_id': setId,
            },
            query: {
                'accounting_period_month': accountingPeriodMonth,
                'accounting_period_year': accountingPeriodYear,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Creates BenefitSetEmployees. Use 'employees' array for bulk creation or 'employee' for single creation. Accounting period is set via query parameters, defaults to next month. If employees have conflicting benefits, set force_retract=true to confirm retraction. If employees have future benefitset assignments, set force_overwrite=true to confirm overwriting them.
     * @returns AssignmentConfirmationRequiredResponse
     * @returns PaginatedBenefitSetEmployeesList
     * @returns AssignmentPartialSuccessResponse
     * @throws ApiError
     */
    public benefitsBenefitSetsEmployeesCreate({
        setId,
        accountingPeriodMonth,
        accountingPeriodYear,
        requestBody,
    }: {
        setId: string,
        accountingPeriodMonth?: number,
        accountingPeriodYear?: number,
        requestBody?: {
            employees?: Array<string>;
            employee?: string;
            /**
             * Set to true to confirm retraction of activated benefits. Required when there are employees with benefits not in the target benefit set.
             */
            force_retract?: boolean;
            /**
             * Set to true to confirm overwriting all future benefit set assignments.
             */
            force_overwrite?: boolean;
        },
    }): CancelablePromise<AssignmentConfirmationRequiredResponse | PaginatedBenefitSetEmployeesList | AssignmentPartialSuccessResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/benefits/benefit-sets/{set_id}/employees/',
            path: {
                'set_id': setId,
            },
            query: {
                'accounting_period_month': accountingPeriodMonth,
                'accounting_period_year': accountingPeriodYear,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BenefitSetEmployees
     * @throws ApiError
     */
    public benefitsBenefitSetsEmployeesRetrieve({
        id,
        setId,
    }: {
        /**
         * A UUID string identifying this benefit set employee.
         */
        id: string,
        setId: string,
    }): CancelablePromise<BenefitSetEmployees> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/benefit-sets/{set_id}/employees/{id}/',
            path: {
                'id': id,
                'set_id': setId,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BenefitSetEmployees
     * @throws ApiError
     */
    public benefitsBenefitSetsEmployeesUpdate({
        id,
        setId,
        requestBody,
    }: {
        /**
         * A UUID string identifying this benefit set employee.
         */
        id: string,
        setId: string,
        requestBody: BenefitSetEmployeesRequest,
    }): CancelablePromise<BenefitSetEmployees> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/benefits/benefit-sets/{set_id}/employees/{id}/',
            path: {
                'id': id,
                'set_id': setId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BenefitSetEmployees
     * @throws ApiError
     */
    public benefitsBenefitSetsEmployeesPartialUpdate({
        id,
        setId,
        requestBody,
    }: {
        /**
         * A UUID string identifying this benefit set employee.
         */
        id: string,
        setId: string,
        requestBody?: PatchedBenefitSetEmployeesRequest,
    }): CancelablePromise<BenefitSetEmployees> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/benefits/benefit-sets/{set_id}/employees/{id}/',
            path: {
                'id': id,
                'set_id': setId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns void
     * @throws ApiError
     */
    public benefitsBenefitSetsEmployeesDestroy({
        id,
        setId,
    }: {
        /**
         * A UUID string identifying this benefit set employee.
         */
        id: string,
        setId: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/benefits/benefit-sets/{set_id}/employees/{id}/',
            path: {
                'id': id,
                'set_id': setId,
            },
        });
    }

    /**
     * Get the benefit set for an employee for a specific month
     * Returns the benefit set an employee will have for a given month.
     * @returns NextMonthBenefitSet
     * @throws ApiError
     */
    public benefitsBenefitSetsNextMonthRetrieve({
        employee,
        accountingPeriodMonth,
        accountingPeriodYear,
    }: {
        /**
         * The ID of the employee
         */
        employee: string,
        accountingPeriodMonth?: number,
        accountingPeriodYear?: number,
    }): CancelablePromise<NextMonthBenefitSet> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/benefit-sets/next-month/',
            query: {
                'accounting_period_month': accountingPeriodMonth,
                'accounting_period_year': accountingPeriodYear,
                'employee': employee,
            },
        });
    }

    /**
     * Get benefit sets stats
     * Returns statistics for each benefit booked by the company, like how many benefit sets it's included in, how many employees are entitled to use it and how many do.
     * @returns BookedBenefitStats
     * @throws ApiError
     */
    public benefitsBenefitSetsStatsRetrieve({
        company,
    }: {
        /**
         * The ID of the company, or 'own' to select the user's own company, if the user has an associated employee
         */
        company: string,
    }): CancelablePromise<BookedBenefitStats> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/benefit-sets/stats/',
            query: {
                'company': company,
            },
        });
    }

    /**
     * Get upcoming benefit sets for an employee
     * Returns upcoming benefit sets for an employee.
     * @returns UpcomingBenefitSetsResponse
     * @throws ApiError
     */
    public benefitsBenefitSetsUpcomingRetrieve({
        employee,
    }: {
        /**
         * The ID of the employee
         */
        employee: string,
    }): CancelablePromise<UpcomingBenefitSetsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/benefit-sets/upcoming/',
            query: {
                'employee': employee,
            },
        });
    }

    /**
     * Available and used benefit budgets list
     * Returns an overview of the given employee's available and spent benefit budget, as well as a breakdown of the used budgets per benefit in the given accounting period
     * @returns BenefitBudgetOverview
     * @throws ApiError
     */
    public benefitsBudgetsList({
        accountingPeriodMonth,
        accountingPeriodYear,
        employee,
    }: {
        accountingPeriodMonth: number,
        accountingPeriodYear: number,
        /**
         * The employee ID whose config should be returned. 'self' will return the user's own config, if the user has an associated employee
         */
        employee: string,
    }): CancelablePromise<BenefitBudgetOverview> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/budgets/',
            query: {
                'accounting_period_month': accountingPeriodMonth,
                'accounting_period_year': accountingPeriodYear,
                'employee': employee,
            },
        });
    }

    /**
     * Used benefit budget by benefit
     * Returns a breakdown of the used budgets per benefit in the given accounting period
     * @returns BenefitBudget
     * @throws ApiError
     */
    public benefitsBudgetsRetrieve({
        accountingPeriodMonth,
        accountingPeriodYear,
        employee,
        id,
    }: {
        accountingPeriodMonth: number,
        accountingPeriodYear: number,
        /**
         * The employee ID whose config should be returned. 'self' will return the user's own config, if the user has an associated employee
         */
        employee: string,
        /**
         * The name of a standard benefit or the UUID of a custom benefit
         */
        id: string,
    }): CancelablePromise<BenefitBudget> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/budgets/{id}/',
            path: {
                'id': id,
            },
            query: {
                'accounting_period_month': accountingPeriodMonth,
                'accounting_period_year': accountingPeriodYear,
                'employee': employee,
            },
        });
    }

    /**
     * @returns BenefitCalculatorResponse
     * @throws ApiError
     */
    public benefitsCalculatorCreate({
        requestBody,
    }: {
        requestBody: BenefitCalculatorRequest,
    }): CancelablePromise<BenefitCalculatorResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/benefits/calculator',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Benefit configuration list
     * Returns the relevant configuration for all standard and custom benefits for the specified employee
     * @returns BenefitConfig
     * @throws ApiError
     */
    public benefitsConfigList({
        employee,
    }: {
        /**
         * The employee ID whose config should be returned. 'self' will return the user's own config, if the user has an associated employee
         */
        employee: string,
    }): CancelablePromise<Array<BenefitConfig>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/config/',
            query: {
                'employee': employee,
            },
        });
    }

    /**
     * Benefit configuration by benefit
     * Returns the relevant benefit configuration for the specified employee
     * @returns BenefitConfig
     * @throws ApiError
     */
    public benefitsConfigRetrieve({
        employee,
        id,
    }: {
        /**
         * The employee ID whose config should be returned. 'self' will return the user's own config, if the user has an associated employee
         */
        employee: string,
        /**
         * The name of a standard benefit or the UUID of a custom benefit
         */
        id: string,
    }): CancelablePromise<BenefitConfig> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/config/{id}/',
            path: {
                'id': id,
            },
            query: {
                'employee': employee,
            },
        });
    }

    /**
     * List custom benefits
     * Returns a list of all custom benefits for the specified company
     * @returns PaginatedCompanyCustomBenefitList
     * @throws ApiError
     */
    public benefitsCustomList({
        company,
        limit,
        offset,
    }: {
        /**
         * The ID of the company, or 'own' to select the user's own company, if the user has an associated employee
         */
        company: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedCompanyCustomBenefitList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/custom/',
            query: {
                'company': company,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Create custom benefit
     * Creates a new custom benefit for the specified company
     * @returns CompanyCustomBenefit
     * @throws ApiError
     */
    public benefitsCustomCreate({
        company,
        requestBody,
    }: {
        /**
         * The ID of the company, or 'own' to select the user's own company, if the user has an associated employee
         */
        company: string,
        requestBody: CompanyCustomBenefitRequest,
    }): CancelablePromise<CompanyCustomBenefit> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/benefits/custom/',
            query: {
                'company': company,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees and their custom benefit assignments
     * @returns PaginatedCompanyCustomBenefitAssignmentEmployeeList
     * @throws ApiError
     */
    public benefitsCustomEmployeeAssignmentsList({
        benefitId,
        assignmentType,
        costCenter,
        department,
        employeeNumber,
        groups,
        limit,
        location,
        offset,
        search,
        startAccountingPeriodMonth,
        startAccountingPeriodYear,
        status,
    }: {
        benefitId: string,
        assignmentType?: string,
        costCenter?: string,
        department?: string,
        employeeNumber?: string,
        groups?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        location?: string,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * A search term.
         */
        search?: string,
        startAccountingPeriodMonth?: number,
        startAccountingPeriodYear?: number,
        /**
         * * `INACTIVE` - INACTIVE
         * * `ACTIVE` - ACTIVE
         * * `PAUSED` - PAUSED
         * * `RESIGNED` - RESIGNED
         * * `INVITATION_PENDING_EMAIL` - INVITATION_PENDING_EMAIL
         * * `INVITATION_PENDING_PDF` - INVITATION_PENDING_PDF
         * * `DEACTIVATION_DELAYED` - DEACTIVATION_DELAYED
         */
        status?: 'ACTIVE' | 'DEACTIVATION_DELAYED' | 'INACTIVE' | 'INVITATION_PENDING_EMAIL' | 'INVITATION_PENDING_PDF' | 'PAUSED' | 'RESIGNED',
    }): CancelablePromise<PaginatedCompanyCustomBenefitAssignmentEmployeeList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/custom/{benefit_id}/employee_assignments/',
            path: {
                'benefit_id': benefitId,
            },
            query: {
                'assignment_type': assignmentType,
                'cost_center': costCenter,
                'department': department,
                'employee_number': employeeNumber,
                'groups': groups,
                'limit': limit,
                'location': location,
                'offset': offset,
                'search': search,
                'start_accounting_period_month': startAccountingPeriodMonth,
                'start_accounting_period_year': startAccountingPeriodYear,
                'status': status,
            },
        });
    }

    /**
     * API endpoint to view and edit employees and their custom benefit assignments
     * @returns CompanyCustomBenefitAssignmentEmployeeUpdate
     * @throws ApiError
     */
    public benefitsCustomEmployeeAssignmentsCreate({
        benefitId,
        requestBody,
    }: {
        benefitId: string,
        requestBody: Array<CustomBenefitPendingEmployeeCreateRequest>,
    }): CancelablePromise<CompanyCustomBenefitAssignmentEmployeeUpdate> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/benefits/custom/{benefit_id}/employee_assignments/',
            path: {
                'benefit_id': benefitId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees and their custom benefit assignments
     * @returns CompanyCustomBenefitAssignmentEmployee
     * @throws ApiError
     */
    public benefitsCustomEmployeeAssignmentsRetrieve({
        benefitId,
        id,
    }: {
        benefitId: string,
        /**
         * A UUID string identifying this employee.
         */
        id: string,
    }): CancelablePromise<CompanyCustomBenefitAssignmentEmployee> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/custom/{benefit_id}/employee_assignments/{id}/',
            path: {
                'benefit_id': benefitId,
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employees and their custom benefit assignments
     * @returns CompanyCustomBenefitAssignmentEmployee
     * @throws ApiError
     */
    public benefitsCustomEmployeeAssignmentsUpdate({
        benefitId,
        id,
        requestBody,
    }: {
        benefitId: string,
        /**
         * A UUID string identifying this employee.
         */
        id: string,
        requestBody?: CompanyCustomBenefitAssignmentEmployeeRequest,
    }): CancelablePromise<CompanyCustomBenefitAssignmentEmployee> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/benefits/custom/{benefit_id}/employee_assignments/{id}/',
            path: {
                'benefit_id': benefitId,
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees and their custom benefit assignments
     * @returns CompanyCustomBenefitAssignmentEmployee
     * @throws ApiError
     */
    public benefitsCustomEmployeeAssignmentsPartialUpdate({
        benefitId,
        id,
        requestBody,
    }: {
        benefitId: string,
        /**
         * A UUID string identifying this employee.
         */
        id: string,
        requestBody?: PatchedCompanyCustomBenefitAssignmentEmployeeRequest,
    }): CancelablePromise<CompanyCustomBenefitAssignmentEmployee> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/benefits/custom/{benefit_id}/employee_assignments/{id}/',
            path: {
                'benefit_id': benefitId,
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees and their custom benefit assignments
     * @returns void
     * @throws ApiError
     */
    public benefitsCustomEmployeeAssignmentsDestroy({
        benefitId,
        id,
    }: {
        benefitId: string,
        /**
         * A UUID string identifying this employee.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/benefits/custom/{benefit_id}/employee_assignments/{id}/',
            path: {
                'benefit_id': benefitId,
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employees and their custom benefit assignments
     * @returns CompanyCustomBenefitAssignmentEmployeeUpdate
     * @throws ApiError
     */
    public benefitsCustomEmployeeAssignmentsDeleteCreate({
        benefitId,
        requestBody,
    }: {
        benefitId: string,
        requestBody: CustomBenefitPendingEmployeeDestroyRequest,
    }): CancelablePromise<CompanyCustomBenefitAssignmentEmployeeUpdate> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/benefits/custom/{benefit_id}/employee_assignments/delete/',
            path: {
                'benefit_id': benefitId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Retrieve custom benefit
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns CompanyCustomBenefitDetail
     * @throws ApiError
     */
    public benefitsCustomRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this company custom benefit.
         */
        id: string,
    }): CancelablePromise<CompanyCustomBenefitDetail> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/custom/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Update custom benefit
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns CompanyCustomBenefitPatch
     * @throws ApiError
     */
    public benefitsCustomUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this company custom benefit.
         */
        id: string,
        requestBody: CompanyCustomBenefitPatchRequest,
    }): CancelablePromise<CompanyCustomBenefitPatch> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/benefits/custom/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns CompanyCustomBenefitPatch
     * @throws ApiError
     */
    public benefitsCustomPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this company custom benefit.
         */
        id: string,
        requestBody?: PatchedCompanyCustomBenefitPatchRequest,
    }): CancelablePromise<CompanyCustomBenefitPatch> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/benefits/custom/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns void
     * @throws ApiError
     */
    public benefitsCustomDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this company custom benefit.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/benefits/custom/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns any
     * @throws ApiError
     */
    public benefitsCustomAvailableConstraintsRetrieve({
        company,
        exclude,
    }: {
        /**
         * Company UUID to get available constraints for
         */
        company: string,
        /**
         * Custom benefit UUID to exclude from constraint check (used when editing)
         */
        exclude?: string,
    }): CancelablePromise<{
        available_constraints?: Array<BenefitEnum>;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/custom/available-constraints/',
            query: {
                'company': company,
                'exclude': exclude,
            },
        });
    }

    /**
     * Get the monthly sum of custom benefits for an employee
     * Returns the monthly sum of custom benefits for an employee.
     * @returns number
     * @throws ApiError
     */
    public benefitsCustomMonthlySumRetrieve({
        employee,
    }: {
        /**
         * The ID of the employee
         */
        employee: string,
    }): CancelablePromise<number> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/custom/monthly-sum/',
            query: {
                'employee': employee,
            },
        });
    }

    /**
     * @returns CompanyBenefitStats
     * @throws ApiError
     */
    public benefitsStatsRetrieve({
        company,
    }: {
        company: string,
    }): CancelablePromise<CompanyBenefitStats> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/benefits/stats',
            query: {
                'company': company,
            },
        });
    }

}
